import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { CardForm } from './CardForm'

import { IsAgentComponent } from '@/components/common'
import { FormNavigation } from '@/components/form-controls'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { Authorization, AuthorizationType } from '@/types/authorizationForm'
import { AuthorizationFormData, FormsGroupsProps } from '@/types/forms/'
import { useFormProgress } from '@/utils'

// eslint-disable-next-line max-lines-per-function
export const StudyAuthorization = ({
    onNext,
    onPrevious,
    isReadOnly = false,
    onBlockedFieldClick
}: FormsGroupsProps): React.ReactElement => {
    const { control, handleSubmit, watch, reset } =
        useForm<AuthorizationFormData>()
    const { t } = useTranslation()
    const { isAgent } = useAuthStore()
    const { idAspirant, idInscription } = useAuthHook()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const { showToast } = useToast()
    const [disable, setDisable] = useState<boolean>(false)
    const progress = useFormProgress<AuthorizationFormData>({
        watch,
        excludedFields: [
            'aspirantId',
            'acceptEmailNotifications',
            'acceptDataProcessing',
            'economicActivity',
            'secondEconomicActivity',
            'isMinor',
            'acceptSensitiveData',
            'id',
            'updatedBy'
        ]
    })
    const onSubmit: SubmitHandler<AuthorizationFormData> = async (data) => {
        const response = await aspirantsApi.postAuthorizationAspiranByScope({
            idAspirant: idAspirant!,
            idInscription: idInscription || undefined,
            updatedBy: idAspirant!,
            authorizations: [
                {
                    authorizationTypeId: AuthorizationType.STUDY_PLAN,
                    isAccepted: Boolean(data.acceptProgramTerms),
                    valueText: ''
                },
                {
                    authorizationTypeId: AuthorizationType.TERMS,
                    isAccepted: Boolean(data.acceptTerms),
                    valueText: ''
                }
            ]
        })

        if (
            response.status === 200 ||
            response.message?.some((msg: any) =>
                msg.messageDesc.includes('ya existe')
            )
        ) {
            showToast({
                title: t('authorization.title'),
                message: t('forms.ui.save'),
                type: 'success'
            })
            onNext()
        }
    }

    useEffect(() => {
        const loadAuthorizationData = async (): Promise<void> => {
            console.log('this is the idAspirant', idAspirant)
            console.log('this is the idInscription', idInscription)
            if (!idAspirant) {
                return
            }
            const responseAuthByInscription =
                await aspirantsApi.getAuthorizationAspirantByInscription(
                    idAspirant,
                    idInscription!
                )

            console.log(
                'this is the responseAuthByInscription',
                responseAuthByInscription
            )

            // TODO: Borrar lógica anterior authorizations
            /* const { data, status } =
                await aspirantsApi.getAuthorizationForm(idAspirant)

            if (status === 200) {
                if (data.acceptProgramTerms && data.acceptTerms) {
                    setDisable(true)
                }
                reset(data)
            }*/

            if (
                responseAuthByInscription.status === 200 &&
                responseAuthByInscription.data
            ) {
                const formData = {
                    acceptTerms: responseAuthByInscription.data.some(
                        (auth: Authorization) =>
                            auth.authorizationType ===
                                AuthorizationType.TERMS && auth.isAccepted
                    ),
                    acceptProgramTerms: responseAuthByInscription.data.some(
                        (auth: Authorization) =>
                            auth.authorizationType ===
                                AuthorizationType.STUDY_PLAN && auth.isAccepted
                    )
                }

                reset(formData)

                if (
                    (formData.acceptProgramTerms && formData.acceptTerms) ||
                    isAgent
                ) {
                    setDisable(true)
                }
            }
        }

        loadAuthorizationData()
    }, [idAspirant, reset])

    return (
        <div className={`relative ${isReadOnly ? 'opacity-60' : ''}`}>
            {isReadOnly && (
                <>
                    <div className="absolute inset-0 z-10 bg-gray-200 bg-opacity-50 cursor-not-allowed" />
                    <div
                        className="absolute inset-0 z-20 bg-transparent cursor-pointer"
                        onClick={onBlockedFieldClick}
                    />
                    <div className="absolute top-2 right-2 z-30 bg-yellow-100 border border-yellow-400 text-yellow-800 px-2 py-1 rounded text-xs">
                        🔒 Campos bloqueados - Click para más información
                    </div>
                </>
            )}
            <IsAgentComponent isAgent={isAgent}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="w-full"
                >
                    <CardForm
                        title={t('authorization.StudyPlan.title')}
                        description={t('authorization.StudyPlan.description')}
                        available={t('authorization.StudyPlan.available')}
                        url="universidadean.edu.co"
                        article={[
                            {
                                subtitle: t('authorization.StudyPlan.subtitle'),
                                text: [
                                    t('authorization.StudyPlan.text'),
                                    t('authorization.StudyPlan.text2')
                                ]
                            }
                        ]}
                        nameControl="acceptProgramTerms"
                        acceptTerms={[t('authorization.StudyPlan.acceptTerms')]}
                        required={t('authorization.StudyPlan.required')}
                        control={control}
                        isDisabled={disable}
                    />

                    <CardForm
                        title={t('authorization.Terms.title')}
                        description={t('authorization.Terms.description')}
                        url="https://universidadean.edu.co/sites/default/files/institucion/reglamentos/016_Reglamento_Estudiantil_DEF_Actualizado.pdf"
                        linkText="Reglamento Estudiantil"
                        available={t('authorization.Terms.available')}
                        nameControl="acceptTerms"
                        acceptTerms={[t('authorization.Terms.acceptTerms')]}
                        required={t('authorization.Terms.required')}
                        control={control}
                        isDisabled={disable}
                    />

                    <FormNavigation
                        onSubmit={handleSubmit(onSubmit)}
                        onPrevious={onPrevious}
                        progress={progress}
                        submitDisabled={isAgent}
                    />
                </form>
            </IsAgentComponent>
        </div>
    )
}
